/* Hardtuned / Monster Cart adapter. Settings bridge: snippets/hardtuned-assets.liquid.
 * Client-side cart UX; checkout enforcement requires a separate backend integration. */
(() => {
    'use strict';
    if (window.htBlackFridayRewards) return;
    const config = JSON.parse(document.getElementById('ht-bf-config').textContent);
    const baseFetch = window.fetch.bind(window);
    const root = window.Shopify?.routes?.root || '/';
    const shippingCountries = new Set((config.shippingCountries || '').split(',').map(code => code.trim().toUpperCase()).filter(Boolean));
    const products = new Map();
    let currentCart = null, currentState = null, running = null, timer, again = false, lastError = '', replayCheckout = false;
    const active = () => config.prizePacksEnabled === true && config.rewardsEnabled === true && (config.testMode || (!!config.startsAt && !!config.endsAt && Date.now() >= Date.parse(config.startsAt) && Date.now() < Date.parse(config.endsAt)));
    function currencyRate(currency) {
      if (currency === config.baseCurrency) return 1;
      const rate = Number(window.Shopify?.currency?.rate);
      if (window.Shopify?.currency?.active !== currency || !Number.isFinite(rate) || rate <= 0) throw new Error('Unable to confirm the market currency. Refresh the page before checkout.');
      return rate;
    }
    function evaluate(cart) {
      const country = window.Shopify?.country || config.country;
      const isAunz = country === 'AU' || country === 'NZ';
      const supported = config.baseCurrency === 'AUD' || config.baseCurrency === 'USD';
      const eligible = active() && supported && (config.baseCurrency === 'AUD' ? isAunz : !isAunz);
      const giftLines = cart.items.filter(item => String(item.variant_id) === String(config.giftVariant));
      const giftCost = giftLines.reduce((sum, item) => sum + item.final_line_price, 0);
      const paid = Math.max(0, cart.total_price - giftCost);
      const rate = currencyRate(cart.currency);
      const basePaid = paid / rate;
      const packs = eligible ? Math.floor((basePaid + 0.000001) / config.step) : 0;
      const shippingEligible = (config.baseCurrency === 'AUD' ? isAunz : config.baseCurrency === 'USD' && shippingCountries.has(country));
      // Match Shopify standard shipping eligibility at the configured threshold.
      const freeShipping = shippingEligible && basePaid >= config.shippingThreshold;
      const state = { country, eligible, paid, rate, basePaid, packs, giftLines, actualPacks: giftLines.reduce((sum,item) => sum + item.quantity, 0), shippingEligible, freeShipping, currency: cart.currency };
      const estimate = estimateShipping(cart,state);
      if (estimate !== null) state.freeShipping = estimate === 0;
      return state;
    }
    async function request(path, body) {
      const response = await baseFetch(root + path, body ? {method:'POST', headers:{'Content-Type':'application/json', Accept:'application/json'}, body:JSON.stringify(body)} : {cache:'no-store', headers:{Accept:'application/json'}});
      const data = await response.json();
      if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || 'Cart rewards could not be updated. Please try again.');
      return data;
    }
    async function product(handle) {
      if (!products.has(handle)) products.set(handle, request('products/' + encodeURIComponent(handle) + '.js').catch(error => { products.delete(handle); throw error; }));
      return products.get(handle);
    }
    async function savings(cart) {
      const paidLines = cart.items.filter(item => String(item.variant_id) !== String(config.giftVariant));
      const handles = [...new Set(paidLines.map(item => item.handle))];
      await Promise.all(handles.map(handle => product(handle)));
      let regularTotal = 0;
      for (const item of paidLines) {
        const data = await products.get(item.handle);
        const variant = data.variants.find(variant => String(variant.id) === String(item.variant_id));
        regularTotal += Math.max(Number(variant?.compare_at_price) || 0, item.original_price) * item.quantity;
      }
      return Math.max(0, regularTotal - cart.total_price);
    }

    function estimateShipping(cart, state) {
      let rules;
      try { rules = JSON.parse(config.shippingRules || '{}'); } catch (_) { return null; }
      if (rules.currency !== config.baseCurrency || !Array.isArray(rules.rates)) return null;
      const countryRates = rules.rates.filter(rule => rule.countries?.includes(state.country));
      const candidates = countryRates.length ? countryRates : rules.rates.filter(rule => rule.countries?.includes('*'));
      const applicable = candidates.filter(rule => Number.isFinite(rule.price) &&
        (rule.minWeight == null || cart.total_weight >= rule.minWeight) &&
        (rule.maxWeight == null || cart.total_weight <= rule.maxWeight));
      if (!applicable.length) return null;
      return Math.round(Math.min(...applicable.map(rule => rule.freeFrom != null && state.basePaid >= rule.freeFrom ? 0 : rule.price)) * state.rate);
    }
    function money(cents, currency) {
      const locale = currency === config.baseCurrency ? (currency === 'AUD' ? 'en-AU' : 'en-US') : (document.documentElement.lang || 'en');
      return new Intl.NumberFormat(locale, {style:'currency', currency}).format(cents / 100);
    }
    function setText(element, text) { if (element.textContent !== text) element.textContent = text; }
    function hideNative(element) {
      if (!element) return;
      if (!element.classList.contains('ht-bf-native-hidden')) element.classList.add('ht-bf-native-hidden');
      if (element.style.getPropertyValue('display') !== 'none') element.style.setProperty('display','none','important');
    }

    // Shopify renders inventory flags server-side; Monster Cart has no native low-stock label.
    let lowStockVariants = new Set();
    async function refreshStock(cart) {
      lowStockVariants = new Set();
      try {
        const response = await baseFetch(root + '?section_id=hardtuned-cart-stock', {cache:'no-store'});
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = JSON.parse(doc.querySelector('[data-ht-cart-stock]')?.textContent || '{}');
        const fingerprint = items => JSON.stringify(items.map(item => [String(item.variant_id || item.id), item.quantity]).sort());
        if (!Array.isArray(data.items) || fingerprint(data.items) !== fingerprint(cart.items)) return;
        lowStockVariants = new Set(data.items.filter(item => item.low === true).map(item => String(item.id)));
      } catch (_) { /* Hide stock messages when inventory cannot be verified. */ }
    }
    function renderStock(cartRoot) {
      cartRoot.querySelectorAll('#mu-items-container [data-variant-id]').forEach(line => {
        let label = line.querySelector('.ht-cart-low-stock');
        const show = lowStockVariants.has(String(line.dataset.variantId));
        if (!show) { if (label) label.remove(); return; }
        if (!label) {
          label = document.createElement('div');
          label.className = 'ht-cart-low-stock';
          const dot = document.createElement('span');
          dot.className = 'ht-cart-low-stock__dot'; dot.setAttribute('aria-hidden','true');
          label.append(dot, document.createTextNode('Last few remaining'));
          line.append(label);
        }
      });
    }

    // Cosmetic only: identify explicitly named Buy N/Get N Free automatic offers.
    // Other promotions and ambiguous line matches retain their native badges.
    function renderDiscountBadges(cartRoot) {
      const normalize = value => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
      const isFreeOffer = title => /\bbuy\s+\d+\s+get\s+\d+\s+free\b/i.test(title);
      const items = currentCart?.items || [];
      const freeOffers = new Set();
      for (const item of items) {
        for (const allocation of item.line_level_discount_allocations || []) {
          const app = allocation.discount_application;
          if (app && app.type !== 'discount_code' && isFreeOffer(app.title) && Number(allocation.amount) > 0) freeOffers.add(normalize(app.title));
        }
      }
      cartRoot.querySelectorAll('#mu-items-container .mu-cart-item[data-variant-id]').forEach(line => {
        const quantity = Number(line.querySelector('.mu-item-quantity > div')?.textContent.trim());
        const variantItems = items.filter(item => String(item.variant_id) === line.dataset.variantId);
        const matches = variantItems.filter(item => item.quantity === quantity);
        const price = line.querySelector('.mu-item-price');
        const shownPrice = price ? [...price.childNodes].filter(node => node.nodeName !== 'DEL').map(node => node.textContent).join('') : '';
        const digits = value => String(value).replace(/[^0-9]/g, '');
        const splitFullPrice = matches.length === 0 && variantItems.length === 1 && variantItems[0].quantity > quantity && quantity > 0 &&
          digits(shownPrice) === digits(money(variantItems[0].original_price * quantity, currentCart.currency));
        line.querySelectorAll('.mu-item-auto-discount').forEach(badge => {
          const title = normalize(badge.querySelector('span')?.textContent);
          const hide = freeOffers.has(title) && (splitFullPrice || (matches.length > 0 && matches.every(item =>
            Array.isArray(item.line_level_discount_allocations) && !item.line_level_discount_allocations.some(allocation =>
              normalize(allocation.discount_application?.title) === title && Number(allocation.amount) > 0))));
          badge.classList.toggle('ht-cart-qualifier-badge', hide);
        });
      });
    }
    let installmentSource, installmentRequest, installmentRequestedAmount;
    function renderInstallments(summary, cart) {
      const amount = cart?.total_price;
      installmentSource ||= document.getElementById('ht-installments-cart-source');
      if (cart?.currency !== 'AUD' || !(amount >= 5000 && amount <= 3000000)) {
        if (installmentSource) installmentSource.style.display = 'none';
        return;
      }
      const badge = installmentSource?.querySelector('ht-installment-badge');
      if (badge?.querySelector('shopify-payment-terms')) {
        const subtotal = installmentSource.querySelector('[data-cart-subtotal]');
        subtotal.hidden = true;
        setText(subtotal, money(amount, 'AUD'));
        if (badge.dataset.cartAmount !== String(amount)) badge.dataset.cartAmount = amount;
        if (installmentSource.parentElement !== summary) summary.append(installmentSource);
        installmentSource.style.display = '';
        return;
      }
      if (!customElements.get('ht-installment-badge') || installmentRequest || installmentRequestedAmount === amount) return;
      installmentRequestedAmount = amount;
      installmentRequest = baseFetch(root, { cache: 'no-store', headers: { Accept: 'text/html' } })
        .then(response => response.ok ? response.text() : '')
        .then(html => {
          const page = new DOMParser().parseFromString(html, 'text/html');
          const source = page.getElementById('ht-installments-cart-source');
          if (!source?.querySelector('shopify-payment-terms')) return;
          installmentSource = document.importNode(source, true);
          const loader = page.querySelector('script[src^="https://cdn.shopify.com/shopifycloud/shop-js/modules/"][src*="loader.payment-terms."]');
          if (loader && !customElements.get('shopify-payment-terms')) {
            const script = document.createElement('script');
            script.type = 'module';
            script.src = loader.src;
            document.head.append(script);
          }
        })
        .catch(() => {})
        .finally(() => {
          installmentRequest = null;
          const currentSummary = document.getElementById('ht-order-summary');
          if (currentSummary) renderInstallments(currentSummary, currentCart);
        });
    }
    function render() {
      const cartRoot = document.getElementById('monster-cart-wrapper');
      if (cartRoot) { renderStock(cartRoot); renderDiscountBadges(cartRoot); }
      if (!cartRoot) return;
      const checkout = cartRoot.querySelector('#mu-checkout-container');
      const itemsContainer = cartRoot.querySelector('#mu-items-container');
      // Monster Cart unmounts these containers in its empty view. Clear our
      // sibling UI before returning, even while an earlier cart sync is pending.
      if (!checkout || !itemsContainer || (currentCart && !currentCart.items.length)) {
        cartRoot.querySelectorAll('#ht-order-summary, #ht-bf-progress, #ht-bf-error').forEach(element => element.remove());
        return;
      }
      if (!currentState) return;
      const subtotal = checkout.querySelector('[class~="mu:subtotal"]');
      if (!subtotal) return;
      let orderSummary = cartRoot.querySelector('#ht-order-summary');
      if (!orderSummary) {
        orderSummary = document.createElement('section'); orderSummary.id = 'ht-order-summary';
        orderSummary.setAttribute('aria-labelledby', 'ht-order-summary-title');
        const heading = document.createElement('h2'); heading.id = 'ht-order-summary-title'; heading.textContent = 'Order Summary';
        const row = document.createElement('div'); row.className = 'ht-order-summary__subtotal';
        const label = document.createElement('span'); label.textContent = 'Subtotal';
        const prices = document.createElement('span'); prices.className = 'ht-order-summary__prices';
        const amount = document.createElement('strong'); amount.className = 'ht-order-summary__amount';
        prices.append(amount); row.append(label, prices); orderSummary.append(heading,row);
        const shippingRow = document.createElement('div'); shippingRow.className = 'ht-order-summary__shipping';
        const shippingName = document.createElement('span'); shippingName.className = 'ht-order-summary__shipping-label'; shippingName.textContent = 'Estimated Shipping';
        const shippingAmount = document.createElement('span'); shippingAmount.className = 'ht-order-summary__shipping-amount';
        shippingRow.append(shippingName,shippingAmount);
        const totalRow = document.createElement('div'); totalRow.className = 'ht-order-summary__total';
        const totalName = document.createElement('strong'); totalName.textContent = 'Total';
        const totalAmount = document.createElement('strong'); totalAmount.className = 'ht-order-summary__total-amount';
        totalRow.append(totalName,totalAmount);
        orderSummary.append(shippingRow,totalRow);
        itemsContainer.after(orderSummary);
      }
      orderSummary.hidden = !currentCart?.items?.length;
      setText(orderSummary.querySelector('.ht-order-summary__amount'), money(currentState.paid + (currentState.saved || 0), currentState.currency));
      const estimatedShipping = estimateShipping(currentCart, currentState);
      setText(orderSummary.querySelector('.ht-order-summary__shipping-label'), estimatedShipping === 0 ? 'Shipping' : 'Estimated Shipping');
      setText(orderSummary.querySelector('.ht-order-summary__shipping-amount'), estimatedShipping === null ? 'Calculated at checkout' : estimatedShipping === 0 ? 'Free' : money(estimatedShipping,currentState.currency));
      setText(orderSummary.querySelector('.ht-order-summary__total-amount'), estimatedShipping === null ? 'Calculated at checkout' : money(currentState.paid + estimatedShipping,currentState.currency));
      renderInstallments(orderSummary, currentCart);
      hideNative(subtotal);
      let summary = document.getElementById('ht-bf-summary');
      if (!summary || !orderSummary.contains(summary)) {
        summary = document.createElement('div'); summary.id = 'ht-bf-summary'; summary.setAttribute('role','status'); summary.setAttribute('aria-live','polite');
        orderSummary.querySelector('.ht-order-summary__shipping').before(summary);
      }
      let savedLabel = summary.querySelector('.ht-bf-saved');
      let shippingLabel = summary.querySelector('.ht-bf-shipping');
      if (!savedLabel || !shippingLabel) {
        summary.replaceChildren();
        savedLabel = document.createElement('span'); savedLabel.className = 'ht-bf-saved';
        shippingLabel = document.createElement('span'); shippingLabel.className = 'ht-bf-shipping';
        summary.append(savedLabel, shippingLabel);
      }
      const hasSavings = currentState.saved > 0;
      savedLabel.hidden = !hasSavings;
      shippingLabel.hidden = true;
      summary.hidden = !hasSavings;
      let savingName = savedLabel.querySelector('.ht-order-summary__saving-name');
      let savingAmount = savedLabel.querySelector('.ht-order-summary__saving-amount');
      if (!savingName || !savingAmount) {
        savingName = document.createElement('span'); savingName.className = 'ht-order-summary__saving-name';
        savingAmount = document.createElement('span'); savingAmount.className = 'ht-order-summary__saving-amount';
        savedLabel.replaceChildren(savingName,savingAmount);
      }
      setText(savingName, config.savedLabel);
      setText(savingAmount, money(-(currentState.saved || 0),currentState.currency));
      setText(shippingLabel, config.shippingLabel);
      for (const element of [...checkout.children]) {
        if (element !== summary && /^(you.ve saved|you saved|savings|total savings)/i.test(element.textContent.trim())) hideNative(element);
      }
      const subtotalPrice = subtotal.querySelector('[data-value]') || [...subtotal.children].reverse().find(element => element.tagName === 'SPAN' && element !== subtotal.firstElementChild);
      if (subtotalPrice) {
        let originalTotal = subtotal.querySelector('.ht-bf-original-total');
        if (!originalTotal) {
          originalTotal = document.createElement('del'); originalTotal.className = 'ht-bf-original-total';
          subtotalPrice.before(originalTotal);
        }
        originalTotal.hidden = !(currentState.saved > 0);
        const originalAmount = money(currentState.paid + (currentState.saved || 0), currentState.currency);
        setText(originalTotal, originalAmount);
        if (originalTotal.getAttribute('aria-label') !== 'Original total ' + originalAmount) originalTotal.setAttribute('aria-label', 'Original total ' + originalAmount);
      }
      let error = document.getElementById('ht-bf-error');
      if (!error || !checkout.contains(error)) { error = document.createElement('div'); error.id = 'ht-bf-error'; error.setAttribute('role','alert'); subtotal.before(error); }
      setText(error, lastError);
      cartRoot.querySelectorAll('[data-variant-id]').forEach(line => {
        if (String(line.dataset.variantId) !== String(config.giftVariant)) return;
        if (!line.classList.contains('ht-bf-gift')) line.classList.add('ht-bf-gift');
        let quantity = line.querySelector('.ht-bf-gift-quantity');
        if (!quantity) {
          quantity = document.createElement('div'); quantity.className = 'ht-bf-gift-quantity';
          line.querySelector('.mu-item-title')?.parentElement.after(quantity);
        }
        setText(quantity, 'Qty: ' + currentState.actualPacks + ' · ' + config.giftLabel);
      });
      const nativeProgress = cartRoot.querySelector('.mu-atu-banner');
      hideNative(nativeProgress);
      let progress = document.getElementById('ht-bf-progress');
      if (!progress || !cartRoot.contains(progress)) {
        progress = document.createElement('div'); progress.id = 'ht-bf-progress';
        const message = document.createElement('div'); message.className = 'ht-bf-progress__message';
        const track = document.createElement('div'); track.className = 'ht-bf-progress__track';
        const fill = document.createElement('span'); fill.className = 'ht-bf-progress__fill'; track.append(fill); progress.append(message,track);
        if (nativeProgress) nativeProgress.before(progress); else cartRoot.querySelector('#mu-items-container')?.before(progress);
      }
      progress.hidden = !currentState.paid || (!currentState.eligible && !currentState.shippingEligible);
      let goal = config.shippingThreshold, message;
      if (currentState.shippingEligible) {
        message = currentState.freeShipping ? 'You\u2019ve unlocked Free Shipping!' : config.shippingProgress.replace('[amount]', money(Math.max(0,Math.ceil((goal - currentState.basePaid) * currentState.rate)),currentState.currency));
      } else {
        goal = (currentState.packs + 1) * config.step;
        message = config.giftProgress.replace('[amount]', money(Math.max(1, Math.ceil((goal - currentState.basePaid) * currentState.rate)),currentState.currency));
      }
      setText(progress.querySelector('.ht-bf-progress__message'), message);
      const width = Math.min(100, currentState.basePaid / goal * 100) + '%';
      if (progress.querySelector('.ht-bf-progress__fill').style.width !== width) progress.querySelector('.ht-bf-progress__fill').style.width = width;
    }
    async function syncOnce() {
      let cart = await request('cart.js');
      if (active() && !config.giftVariant) throw new Error('The Black Friday Prize Pack is unavailable on this store.');
      const state = evaluate(cart);
      if (state.packs && !config.giftAvailable) throw new Error('The Black Friday Prize Pack is currently out of stock.');
      if (state.actualPacks !== state.packs || state.giftLines.length > 1) {
        if (state.giftLines.length) {
          const updates = {};
          state.giftLines.forEach((line,index) => { updates[line.key] = index === 0 ? state.packs : 0; });
          await request('cart/update.js', {updates});
        } else if (state.packs) {
          await request('cart/add.js', {items:[{id:config.giftVariant,quantity:state.packs,properties:{_ht_bf_reward:'2026',_ht_bf_test:config.testMode ? 'true' : 'false'}}]});
        }
        cart = await request('cart.js');
        if (typeof window.monster_setCartItems === 'function') await window.monster_setCartItems(cart.items);
        if (typeof window.updateCartCounters === 'function') window.updateCartCounters();
      }
      currentCart = cart;
      currentState = evaluate(cart);
      if (currentState.actualPacks !== currentState.packs) { again = true; return; }
      await Promise.all([refreshStock(cart), savings(cart).then(saved => { currentState.saved = saved; })]);
      lastError = '';
      render();
    }
    function sync() {
      clearTimeout(timer);
      if (running) { again = true; return running; }
      const work = async () => {
        let passes = 0;
        do {
          again = false; await syncOnce(); passes++;
          if (again && passes >= 4) throw new Error('Cart changed while rewards were updating. Please try again.');
        } while (again);
      };
      running = (navigator.locks ? navigator.locks.request('ht-bf-cart-rewards',work) : work())
        .catch(error => { lastError = error.message; render(); console.error('Hardtuned cart rewards:',error); throw error; })
        .finally(() => { running = null; });
      return running;
    }
    function schedule() { clearTimeout(timer); timer = setTimeout(() => sync().catch(() => {}),350); }
    function isCartWrite(url) {
      try { return /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(new URL(url,location.origin).pathname); } catch (_) { return false; }
    }
    window.fetch = function(input,options) {
      const result = baseFetch(input,options);
      const url = typeof input === 'string' || input instanceof URL ? String(input) : input?.url;
      if (isCartWrite(url)) result.then(response => { if (response.ok) schedule(); },() => {});
      return result;
    };
    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method,url,...args) {
      if (isCartWrite(String(url))) this.addEventListener('load',() => { if (this.status >= 200 && this.status < 300) schedule(); },{once:true});
      return originalOpen.call(this,method,url,...args);
    };
    document.addEventListener('click',async event => {
      const target = event.target.closest?.('#mu-checkout-button, button[name="checkout"], a[href="/checkout"]');
      if (!target || replayCheckout || (!active() && !currentState?.actualPacks)) return;
      event.preventDefault(); event.stopImmediatePropagation();
      try {
        await sync();
        replayCheckout = true;
        target.click();
      } catch (_) { render(); }
      finally { replayCheckout = false; }
    },true);
    document.addEventListener('click',event => { if (event.target.closest?.('#cart-counter')) schedule(); });
    document.addEventListener('visibilitychange',() => { if (!document.hidden) schedule(); });
    window.addEventListener('pageshow',schedule);
    function cartFingerprint() {
      const cartRoot = document.getElementById('monster-cart-wrapper');
      if (!cartRoot) return '';
      const lines = [...cartRoot.querySelectorAll('#mu-items-container [data-variant-id]')].map(line => [
        line.dataset.variantId,
        line.querySelector('.mu-item-quantity > div')?.textContent.trim(),
        line.querySelector('.mu-item-price')?.textContent.trim()
      ]);
      const total = cartRoot.querySelector('[class~="mu:subtotal"] [data-value]')?.getAttribute('data-value');
      return JSON.stringify([lines,total]);
    }
    let renderQueued = false, lastCartFingerprint = '';
    new MutationObserver(() => {
      if (renderQueued) return;
      renderQueued = true;
      requestAnimationFrame(() => {
        renderQueued = false;
        const fingerprint = cartFingerprint();
        if (fingerprint && fingerprint !== lastCartFingerprint) {
          lastCartFingerprint = fingerprint;
          schedule();
        }
        render();
      });
    }).observe(document.body,{childList:true,subtree:true});
    window.htBlackFridayRewards = {sync};
    schedule();
  })();